import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { Button } from 'src/app/components/lib/button';
import { Drawer } from 'src/app/components/lib/drawer';
import { Loader } from 'src/app/components/lib/loader';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import ErrorBoundary from 'src/app/layout/error/error-boundary';

import { ExitModal } from './exit-modal';
import { TopNavigation } from './top-navigation';
import { createNewStepStatusMap, findNextActiveStepIndex, findPreviousActiveStepIndex } from './utils';
import {
  BodyContainer,
  ButtonsContainer,
  DrawerContainer,
  LeftPanel,
  LoaderContainer,
  MainBodyContainer,
  NavigationContainer,
  SidebarContainer,
} from './wizard.styles';

import type { StepStatus } from './utils';

export type WizardStepComponentProps = {
  currentIndex: number;
  currentStep: string;
  disableCompleteButton: () => void;
  disableNextButton: () => void;
  disableSaveAndExit: () => void;
  disableStepByIndex: (stepIndex: number) => void;
  enableCompleteButton: () => void;
  enableNextButton: () => void;
  enableSaveAndExit: () => void;
  enableStepByIndex: (stepIndex: number) => void;
  hideBottomNavigation: () => void;
  navigateToDeadEnd: ({ destinationStep, sourceStep }: { destinationStep: number; sourceStep: number }) => void;
  navigateToStep: (stepIndex: number) => void;
  onNextButtonClick: (arg: () => Promise<void>) => void;
  resetCompleteButtonOverride: () => void;
  setActiveStepComplete: () => void;
  setComplete: () => void;
  setIncomplete: () => void;
  setSubstepActive: () => void;
  showBottomNavigation: () => void;
  stepStatus: StepStatus;
  stepStatusMap: Record<number, StepStatus>;
  substepActive: boolean;
};

export type WizardStep = {
  Component: (arg: WizardStepComponentProps) => React.ReactElement;
  hideBackButton?: boolean;
  shouldFill?: boolean;
  title: string;
};

export type WizardProps = {
  completeButtonDisabled?: boolean;
  completeButtonLabel: string;
  Configuration?: React.ReactElement;
  defaultStepToOpenOn?: number;
  disableResetOnExit?: boolean;
  drawerAriaLabel?: string;
  enableNavigationByClicks?: boolean;
  exitButtonLabel?: string;
  isCompleteRequestInFlight?: boolean;
  loading?: boolean;
  NavigationContent?: React.ReactElement | null;
  nextButtonTooltip?: string;
  onClose: (() => Promise<void>) | (() => void);
  onCompleteButtonClick: (reset: () => void) => Promise<void>; // this returns a promise so we can await network calls or any other async action before closing and resetting the wizard
  onSave?: () => Promise<void>;
  onStepChange?: (stepTitle: string, stepIndex: number) => void;
  open: boolean;
  showConfiguration?: boolean;
  showWarningOnExit?: boolean;
  Sidebar?: () => React.ReactElement;
  steps: WizardStep[];
  subtitle?: ReactNode;
  title?: React.ReactElement | string;
  titleFontSize?: string;
  titleMinWidth?: string;
  warningText?: React.ReactElement;
};

export function Wizard({
  completeButtonDisabled = false,
  completeButtonLabel,
  Configuration,
  defaultStepToOpenOn = 0,
  disableResetOnExit = false,
  drawerAriaLabel,
  enableNavigationByClicks = false,
  exitButtonLabel = 'Exit',
  isCompleteRequestInFlight = false,
  loading = false,
  NavigationContent,
  nextButtonTooltip,
  onClose,
  onCompleteButtonClick,
  onSave,
  onStepChange,
  open,
  showConfiguration = false,
  showWarningOnExit = false,
  Sidebar,
  steps,
  subtitle,
  title,
  titleFontSize,
  titleMinWidth,
  warningText,
}: WizardProps) {
  // internal state
  const [activeStep, setActiveStep] = useState(0);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [deadEnd, setDeadEnd] = useState({ sourceStep: 0, isDeadEnd: false });
  const [stepStatusMap, setStepStatusMap] = useState<Record<number, StepStatus>>(createNewStepStatusMap(steps.length));
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [substepActive, setSubstepActive] = useState(false);
  const initialSteps = useRef(steps);
  const [nextButtonClickCallback, setNextButtonClickCallback] = useState<(() => Promise<void>) | undefined>(undefined);
  const discountWizardEnabled = useLDFlag<boolean>(LD.DISCOUNT_WIZARD);

  const [overrideCompleteButtonState, setOverrideCompleteButtonState] = useState<boolean | undefined>(undefined);
  const [hideBottomNav, setHideBottomNav] = useState(false);
  const [saveAndExitDisabled, setSaveAndExitDisabled] = useState<boolean | undefined>(undefined);

  // lifecycle hooks
  useEffect(() => {
    // when the step prop updates (i.e. from the configuration screen), we need to recreate our status map
    if (initialSteps.current !== steps) {
      setStepStatusMap(createNewStepStatusMap(steps.length));
      initialSteps.current = steps;
    }
  }, [steps]);

  useEffect(() => {
    if (defaultStepToOpenOn !== 0) {
      // Set the active step directly instead of using navigateToDeadEnd
      setActiveStep(defaultStepToOpenOn);

      // Mark all previous steps as complete when skipping to a later step
      for (let stepIndex = 0; stepIndex < defaultStepToOpenOn; stepIndex++) {
        setStepStatusMap((status) => ({
          ...status,
          [stepIndex]: { ...status[stepIndex], enabled: true, complete: true, nextEnabled: true },
        }));
      }
    }
  }, [defaultStepToOpenOn, steps]);

  // helper functions
  const enableNextButton = useCallback(() => {
    setStepStatusMap((status) => ({ ...status, [activeStep]: { ...status[activeStep], nextEnabled: true } }));
  }, [activeStep]);
  const disableNextButton = useCallback(() => {
    setStepStatusMap((status) => ({ ...status, [activeStep]: { ...status[activeStep], nextEnabled: false } }));
  }, [activeStep]);
  const setActiveStepComplete = useCallback(() => {
    setStepStatusMap((status) => ({ ...status, [activeStep]: { ...status[activeStep], complete: true } }));
  }, [activeStep]);
  const setActiveStepIncomplete = useCallback(() => {
    setStepStatusMap((status) => ({ ...status, [activeStep]: { ...status[activeStep], complete: false } }));
  }, [activeStep]);
  const disableStepByIndex = useCallback((stepIndex: number) => {
    setStepStatusMap((status) => ({ ...status, [stepIndex]: { ...status[stepIndex], disabled: true } }));
  }, []);
  const enableStepByIndex = useCallback((stepIndex: number) => {
    setStepStatusMap((status) => ({ ...status, [stepIndex]: { ...status[stepIndex], disabled: false } }));
  }, []);
  const enableCompleteButton = useCallback(() => {
    setOverrideCompleteButtonState(false);
  }, []);
  const disableCompleteButton = useCallback(() => {
    setOverrideCompleteButtonState(true);
  }, []);
  const resetCompleteButtonOverride = useCallback(() => {
    setOverrideCompleteButtonState(undefined);
  }, []);
  const enableSaveAndExit = useCallback(() => {
    setSaveAndExitDisabled(false);
  }, []);
  const disableSaveAndExit = useCallback(() => {
    setSaveAndExitDisabled(true);
  }, []);

  const resetWizard = () => {
    setActiveStep(0);
    setStepStatusMap(createNewStepStatusMap(steps.length));
    setNextButtonClickCallback(undefined);
    setSubstepActive(false);

    if (discountWizardEnabled) {
      setDeadEnd({ isDeadEnd: false, sourceStep: 0 });
    }
  };

  // conditional UI
  const BodyContents = useMemo(() => {
    if (showConfiguration && Configuration !== undefined) {
      return () => Configuration;
    }
    return (props: WizardStepComponentProps) => steps[activeStep].Component(props);
  }, [activeStep, Configuration, showConfiguration, steps]);

  const activeStepShouldFill = steps[activeStep].shouldFill;

  // conditional render logic
  const isFirstStepActive = activeStep === 0;
  const isFinalStepActive = activeStep === steps.length - 1; // steps are zero indexed
  const showSidebar = !!Sidebar && !showConfiguration && !isFinalStepActive;
  const shouldShowBottomNavigation = !showConfiguration && !hideBottomNav;
  const primaryButtonDisabled = useMemo(() => {
    if (isFinalStepActive && overrideCompleteButtonState !== undefined) {
      return overrideCompleteButtonState;
    }
    return (
      isCompleteRequestInFlight ||
      ((isFinalStepActive ? completeButtonDisabled : !stepStatusMap[activeStep]?.nextEnabled || loading) &&
        !substepActive)
    );
  }, [
    isCompleteRequestInFlight,
    isFinalStepActive,
    completeButtonDisabled,
    stepStatusMap,
    activeStep,
    loading,
    substepActive,
    overrideCompleteButtonState,
  ]);
  const backButtonDisabled = isCompleteRequestInFlight || isFirstStepActive || loading;
  const backButtonVisible = !steps[activeStep].hideBackButton && !substepActive && !deadEnd.isDeadEnd;
  const primaryButtonLabel = useMemo(() => {
    if (substepActive || deadEnd.isDeadEnd) {
      return 'Done';
    }

    if (isFinalStepActive) {
      return completeButtonLabel;
    }

    return 'Next';
  }, [completeButtonLabel, deadEnd.isDeadEnd, isFinalStepActive, substepActive]);

  // handlers
  const onPrimaryButtonClick = async () => {
    if (substepActive) {
      setSubstepActive(false);
      return;
    }

    if (deadEnd.isDeadEnd) {
      setActiveStep(deadEnd.sourceStep);
      setDeadEnd({ isDeadEnd: false, sourceStep: 0 });
      return;
    }

    if (isFinalStepActive) {
      await onCompleteButtonClick(() => resetWizard());
      return;
    }

    await nextButtonClickCallback?.();
    setActiveStep((current) => findNextActiveStepIndex({ current, stepStatusMap }));
    setNextButtonClickCallback(undefined);
  };

  const onStepClick = (stepIndex: number) => {
    setActiveStep(stepIndex);
  };

  const onBackButtonClick = () => {
    setActiveStep((current) => findPreviousActiveStepIndex({ current, stepStatusMap }));
  };

  const onExit = () => {
    if (showWarningOnExit) {
      setExitModalVisible(true);
    } else {
      void exit();
    }
  };

  const exit = async () => {
    await onClose();
    if (!disableResetOnExit) {
      resetWizard();
    }
  };

  const navigateToDeadEnd: WizardStepComponentProps['navigateToDeadEnd'] = ({ destinationStep, sourceStep }) => {
    setActiveStep(destinationStep);
    setDeadEnd({ isDeadEnd: true, sourceStep });
  };

  const hideBottomNavigation = useCallback(() => {
    setHideBottomNav(true);
  }, []);

  const showBottomNavigation = useCallback(() => {
    setHideBottomNav(false);
  }, []);

  useEffect(() => {
    setCurrentStep(steps[activeStep].title);
    onStepChange?.(steps[activeStep].title, activeStep);
  }, [activeStep, steps, onStepChange]);

  return (
    <>
      <Drawer ariaLabel={drawerAriaLabel} fullScreen open={open}>
        <DrawerContainer>
          <TopNavigation
            activeStep={activeStep}
            enableNavigationByClicks={enableNavigationByClicks}
            exitButtonLabel={exitButtonLabel}
            loading={loading || isCompleteRequestInFlight}
            saveAndExitDisabled={saveAndExitDisabled}
            showSteps={!showConfiguration}
            steps={steps}
            stepStatusMap={stepStatusMap}
            subtitle={subtitle}
            title={title}
            titleFontSize={titleFontSize}
            titleMinWidth={titleMinWidth}
            onExit={onExit}
            onSave={onSave}
            onStepClick={(step: number) => onStepClick(step)}
          />
          <ErrorBoundary>
            <BodyContainer $sidebarVisible={showSidebar}>
              <LeftPanel $navigationVisible={shouldShowBottomNavigation}>
                <MainBodyContainer $fullWidthAndHeight={!!activeStepShouldFill}>
                  {loading && (
                    <LoaderContainer>
                      <Loader size='5x' variant='black' />
                    </LoaderContainer>
                  )}
                  {!loading && (
                    <BodyContents
                      currentIndex={activeStep}
                      currentStep={currentStep}
                      disableCompleteButton={disableCompleteButton}
                      disableNextButton={disableNextButton}
                      disableSaveAndExit={disableSaveAndExit}
                      disableStepByIndex={disableStepByIndex}
                      enableCompleteButton={enableCompleteButton}
                      enableNextButton={enableNextButton}
                      enableSaveAndExit={enableSaveAndExit}
                      enableStepByIndex={enableStepByIndex}
                      hideBottomNavigation={hideBottomNavigation}
                      navigateToDeadEnd={navigateToDeadEnd}
                      navigateToStep={setActiveStep}
                      resetCompleteButtonOverride={resetCompleteButtonOverride}
                      setActiveStepComplete={setActiveStepComplete}
                      setComplete={setActiveStepComplete}
                      setIncomplete={setActiveStepIncomplete}
                      setSubstepActive={() => setSubstepActive(true)}
                      showBottomNavigation={showBottomNavigation}
                      stepStatus={stepStatusMap[activeStep]}
                      stepStatusMap={stepStatusMap}
                      substepActive={substepActive}
                      onNextButtonClick={(callback) => setNextButtonClickCallback(() => callback)}
                    />
                  )}
                </MainBodyContainer>
                {shouldShowBottomNavigation && (
                  <NavigationContainer $singleButton={!backButtonVisible}>
                    {backButtonVisible && (
                      <Button
                        disabled={backButtonDisabled}
                        label='Back'
                        variant='secondary'
                        onClick={onBackButtonClick}
                      />
                    )}
                    <ButtonsContainer>
                      {NavigationContent}
                      <Button
                        disabled={primaryButtonDisabled}
                        label={primaryButtonLabel}
                        loading={isCompleteRequestInFlight}
                        tooltipPlacement='left'
                        tooltipText={nextButtonTooltip}
                        onClick={onPrimaryButtonClick}
                      />
                    </ButtonsContainer>
                  </NavigationContainer>
                )}
              </LeftPanel>
              {showSidebar && (
                <SidebarContainer>
                  <Sidebar />
                </SidebarContainer>
              )}
            </BodyContainer>
          </ErrorBoundary>
        </DrawerContainer>
      </Drawer>
      <ExitModal
        open={exitModalVisible}
        text={warningText}
        onClose={() => setExitModalVisible(false)}
        onExitWithoutSaving={() => {
          setExitModalVisible(false);
          void exit();
        }}
      />
    </>
  );
}
